import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2, Plus, Trash2, Eye, EyeOff, DollarSign, Image, History, ChevronDown, ChevronUp, User, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const SECTION_BADGES = {
  food: { label: 'Food', cls: 'bg-[#BA1826] text-white' },
  hygiene: { label: 'Hygiene', cls: 'bg-[#0A0A0A] text-white' },
  both: { label: 'Food + Hygiene', cls: 'bg-[#7C2D12] text-white' },
};

const DiffTable = ({ title, oldItems = [], newItems = [], oldTotal = 0, newTotal = 0 }) => {
  // Build a merged key set on item name
  const byName = (arr) => Object.fromEntries((arr || []).map((i) => [i.name, i]));
  const oldMap = byName(oldItems);
  const newMap = byName(newItems);
  const names = Array.from(new Set([...Object.keys(oldMap), ...Object.keys(newMap)]));
  return (
    <div className="bg-white border border-[#E5E7EB] rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <h5 className="font-heading text-xs font-bold text-[#0A0A0A]">{title}</h5>
        <div className="text-[11px] text-[#4B5563]">
          Total: <span className="font-semibold text-[#0A0A0A]">${parseFloat(oldTotal).toFixed(2)}</span>
          <ArrowRight className="inline w-3 h-3 mx-1 text-[#4B5563]" />
          <span className="font-semibold text-[#BA1826]">${parseFloat(newTotal).toFixed(2)}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[9px] uppercase text-[#4B5563] border-b border-[#E5E7EB]">
              <th className="py-1 pr-2">Item</th>
              <th className="py-1 pr-2">Qty (old → new)</th>
              <th className="py-1 pr-2">$/Unit (old → new)</th>
              <th className="py-1 pr-2">Total (old → new)</th>
              <th className="py-1 pr-2">Change</th>
            </tr>
          </thead>
          <tbody>
            {names.length === 0 && (
              <tr><td colSpan={5} className="py-2 text-center text-[#94A3B8]">No items</td></tr>
            )}
            {names.map((name) => {
              const o = oldMap[name];
              const n = newMap[name];
              const changeKind = !o ? 'added' : !n ? 'removed' : (
                (o.quantity !== n.quantity || o.cost_per_unit !== n.cost_per_unit || o.total !== n.total) ? 'changed' : 'same'
              );
              const chipCls = {
                added: 'bg-green-100 text-green-700',
                removed: 'bg-red-100 text-red-700',
                changed: 'bg-amber-100 text-amber-700',
                same: 'bg-gray-100 text-gray-500',
              }[changeKind];
              return (
                <tr key={name} className="border-b border-[#F1F5F9] last:border-0">
                  <td className="py-1 pr-2 font-medium text-[#0A0A0A]">{name}</td>
                  <td className="py-1 pr-2 text-[#4B5563]">{o?.quantity ?? '—'} → {n?.quantity ?? '—'}</td>
                  <td className="py-1 pr-2 text-[#4B5563]">${parseFloat(o?.cost_per_unit || 0).toFixed(2)} → ${parseFloat(n?.cost_per_unit || 0).toFixed(2)}</td>
                  <td className="py-1 pr-2 text-[#4B5563]">${parseFloat(o?.total || 0).toFixed(2)} → ${parseFloat(n?.total || 0).toFixed(2)}</td>
                  <td className="py-1 pr-2"><span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${chipCls}`}>{changeKind}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GlobalContentAuditLog = ({ refreshSignal }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/global-content-audit?limit=100`, { withCredentials: true });
      setEntries(res.data || []);
    } catch {
      setEntries([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries, refreshSignal]);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid="global-audit-log">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-[#BA1826]" />
          <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Cost Change Audit Log</h3>
          <span className="text-[10px] text-[#4B5563] bg-[#F1F5F9] px-2 py-0.5 rounded">Global Country Program Content</span>
        </div>
        <button onClick={fetchEntries} className="text-[10px] text-[#BA1826] font-semibold hover:underline" data-testid="refresh-audit-log">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-[#E5E7EB] rounded">
          <p className="text-xs text-[#4B5563]">No changes logged yet. Updates to Food Bucket or Hygiene Essentials will be tracked here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="audit-log-table">
            <thead>
              <tr className="text-left text-[9px] uppercase text-[#4B5563] border-b border-[#E5E7EB]">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Section</th>
                <th className="py-2 pr-3">Old Total</th>
                <th className="py-2 pr-3">New Total</th>
                <th className="py-2 pr-3">Cost/Family</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const isOpen = expanded === e.id;
                const badge = SECTION_BADGES[e.section] || SECTION_BADGES.both;
                const oldCpf = parseFloat(e.old_cost_per_family || 0);
                const newCpf = parseFloat(e.new_cost_per_family || 0);
                const cpfDelta = newCpf - oldCpf;
                return (
                  <React.Fragment key={e.id}>
                    <tr className="border-b border-[#F1F5F9]" data-testid={`audit-row-${e.id}`}>
                      <td className="py-2 pr-3 text-[#4B5563] whitespace-nowrap">{formatDate(e.timestamp)}</td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5">
                          <User className="w-3 h-3 text-[#4B5563]" />
                          <div>
                            <div className="text-[#0A0A0A] font-medium">{e.user_email || '—'}</div>
                            {e.user_role && <div className="text-[9px] text-[#94A3B8] uppercase">{e.user_role.replace('_', ' ')}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-semibold uppercase ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="py-2 pr-3 text-[#4B5563]">
                        Food ${parseFloat(e.old_food_total || 0).toFixed(2)}<br />
                        <span className="text-[10px]">Hyg ${parseFloat(e.old_hygiene_total || 0).toFixed(2)}</span>
                      </td>
                      <td className="py-2 pr-3 text-[#0A0A0A] font-medium">
                        Food ${parseFloat(e.new_food_total || 0).toFixed(2)}<br />
                        <span className="text-[10px]">Hyg ${parseFloat(e.new_hygiene_total || 0).toFixed(2)}</span>
                      </td>
                      <td className="py-2 pr-3">
                        <span className="text-[#4B5563]">${oldCpf.toFixed(2)}</span>
                        <ArrowRight className="inline w-3 h-3 mx-1 text-[#4B5563]" />
                        <span className="font-bold text-[#BA1826]">${newCpf.toFixed(2)}</span>
                        {cpfDelta !== 0 && (
                          <span className={`ml-1 text-[9px] font-semibold ${cpfDelta > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            ({cpfDelta > 0 ? '+' : ''}{cpfDelta.toFixed(2)})
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <button
                          onClick={() => setExpanded(isOpen ? null : e.id)}
                          className="text-[10px] text-[#BA1826] font-semibold hover:underline flex items-center gap-0.5"
                          data-testid={`toggle-details-${e.id}`}
                        >
                          {isOpen ? <><ChevronUp className="w-3 h-3" /> Hide</> : <><ChevronDown className="w-3 h-3" /> View Details</>}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#FEFCE8]" data-testid={`audit-details-${e.id}`}>
                        <td colSpan={7} className="p-4">
                          <div className="space-y-3">
                            {(e.section === 'food' || e.section === 'both') && (
                              <DiffTable
                                title="Food Bucket — item-level changes"
                                oldItems={e.old_snapshot?.food_bucket?.items}
                                newItems={e.new_snapshot?.food_bucket?.items}
                                oldTotal={e.old_food_total}
                                newTotal={e.new_food_total}
                              />
                            )}
                            {(e.section === 'hygiene' || e.section === 'both') && (
                              <DiffTable
                                title="Hygiene Essentials Kit — item-level changes"
                                oldItems={e.old_snapshot?.hygiene_kit?.items}
                                newItems={e.new_snapshot?.hygiene_kit?.items}
                                oldTotal={e.old_hygiene_total}
                                newTotal={e.new_hygiene_total}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// Reusable editor for a section with optional eyebrow, heading, and a list of paragraphs
const ParagraphsEditor = ({ title, testIdPrefix, eyebrow, heading, paragraphs, onEyebrowChange, onHeadingChange, onParagraphsChange }) => {
  const updateParagraph = (idx, value) => {
    const updated = [...paragraphs];
    updated[idx] = value;
    onParagraphsChange(updated);
  };
  const addParagraph = () => onParagraphsChange([...paragraphs, '']);
  const removeParagraph = (idx) => onParagraphsChange(paragraphs.filter((_, i) => i !== idx));

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid={`${testIdPrefix}-editor`}>
      <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">{title}</h4>
      {onEyebrowChange && (
        <div>
          <label className="text-[10px] text-[#4B5563] mb-1 block">Eyebrow (small red text)</label>
          <input type="text" value={eyebrow || ''} onChange={(e) => onEyebrowChange(e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
            data-testid={`${testIdPrefix}-eyebrow-input`} />
        </div>
      )}
      <div>
        <label className="text-[10px] text-[#4B5563] mb-1 block">Heading</label>
        <input type="text" value={heading || ''} onChange={(e) => onHeadingChange(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
          data-testid={`${testIdPrefix}-heading-input`} />
      </div>
      <div className="space-y-2">
        <label className="text-[10px] text-[#4B5563] mb-1 block">Paragraphs ({paragraphs.length})</label>
        {paragraphs.map((p, i) => (
          <div key={i} className="flex gap-2">
            <textarea value={p} onChange={(e) => updateParagraph(i, e.target.value)}
              rows={3} className="flex-1 px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-y"
              data-testid={`${testIdPrefix}-paragraph-${i}`} />
            <button type="button" onClick={() => removeParagraph(i)}
              className="text-red-400 hover:text-red-600 p-1 self-start"
              data-testid={`${testIdPrefix}-remove-paragraph-${i}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={addParagraph}
          className="text-[10px] text-[#BA1826] font-semibold hover:underline flex items-center gap-1"
          data-testid={`${testIdPrefix}-add-paragraph`}>
          <Plus className="w-3 h-3" /> Add Paragraph
        </button>
      </div>
    </div>
  );
};


const BreakdownEditor = ({ title, data, onChange, showSize = false }) => {
  const items = data?.items || [];

  const autoCalc = (updatedItems) => {
    // Auto-calculate item totals only when quantity is purely numeric
    const recalced = updatedItems.map(item => {
      const qtyStr = String(item.quantity).trim();
      const isNumericQty = /^\d+(\.\d+)?$/.test(qtyStr);
      if (isNumericQty && item.cost_per_unit > 0) {
        const autoTotal = Math.round(parseFloat(qtyStr) * item.cost_per_unit * 100) / 100;
        return { ...item, total: autoTotal };
      }
      return item;
    });
    const sectionTotal = Math.round(recalced.reduce((s, item) => s + (item.total || 0), 0) * 100) / 100;
    onChange({ ...data, items: recalced, total: sectionTotal });
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: field === 'name' || field === 'quantity' ? value : parseFloat(value) || 0 };
    autoCalc(updated);
  };
  const addItem = () => {
    const updated = [...items, { name: '', quantity: '1', cost_per_unit: 0, total: 0 }];
    autoCalc(updated);
  };
  const removeItem = (idx) => {
    const updated = items.filter((_, i) => i !== idx);
    autoCalc(updated);
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
      <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">{title}</h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-[#4B5563] mb-1 block">Section Title</label>
          <input type="text" value={data?.title || ''} onChange={(e) => onChange({ ...data, title: e.target.value })}
            className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] text-[#4B5563] mb-1 block">Total Label</label>
          <input type="text" value={data?.total_label || ''} onChange={(e) => onChange({ ...data, total_label: e.target.value })}
            className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
        </div>
        {showSize && (
          <div>
            <label className="text-[10px] text-[#4B5563] mb-1 block">Bucket Size (e.g. "5-gallon")</label>
            <input type="text" value={data?.size || ''} onChange={(e) => onChange({ ...data, size: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
          </div>
        )}
      </div>
      <div>
        <label className="text-[10px] text-[#4B5563] mb-1 block">Section Total (auto-calculated)</label>
        <div className="w-32 px-2 py-1.5 text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded font-bold text-[#BA1826]">
          ${data?.total?.toFixed(2) || '0.00'}
        </div>
      </div>
      {/* Items */}
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_80px_80px_80px_30px] gap-1 text-[9px] font-semibold text-[#4B5563] uppercase px-1">
          <span>Item</span><span>Qty</span><span>$/Unit</span><span>Total</span><span></span>
        </div>
        {items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_80px_80px_80px_30px] gap-1 items-center">
            <input type="text" value={item.name} onChange={(e) => updateItem(idx, 'name', e.target.value)}
              className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" placeholder="Item name" />
            <input type="text" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
              className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none text-center" placeholder="Qty" />
            <input type="number" step="0.01" value={item.cost_per_unit} onChange={(e) => updateItem(idx, 'cost_per_unit', e.target.value)}
              className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none text-right" />
            {/^\d+(\.\d+)?$/.test(String(item.quantity).trim()) ? (
              <div className="px-2 py-1.5 text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded text-right font-semibold text-[#0A0A0A]">
                ${item.total?.toFixed(2)}
              </div>
            ) : (
              <input type="number" step="0.01" value={item.total} onChange={(e) => updateItem(idx, 'total', e.target.value)}
                className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none text-right font-semibold" />
            )}
            <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 p-1">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={addItem} className="text-[10px] text-[#BA1826] font-semibold hover:underline flex items-center gap-1 mt-1">
          <Plus className="w-3 h-3" /> Add Item
        </button>
      </div>
    </div>
  );
};

const AdminPageContentTab = () => {
  const [content, setContent] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [globalCountry, setGlobalCountry] = useState(null);
  const [homeHero, setHomeHero] = useState(null);
  const [homeCta, setHomeCta] = useState(null);
  const [aboutPage, setAboutPage] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const [imageConfig, setImageConfig] = useState(null);
  const [localProgramsCards, setLocalProgramsCards] = useState(null);
  const [foodBucket, setFoodBucket] = useState(null);
  const [hygieneKit, setHygieneKit] = useState(null);
  const [globalNotice, setGlobalNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditRefresh, setAuditRefresh] = useState(0);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const ids = ['local_cost_breakdown', 'donation_tiers', 'global_country_program_content', 'home_hero', 'home_cta', 'about_page', 'site_announcement', 'image_display_config', 'local_programs_cards', 'food_bucket_section', 'hygiene_kit_section', 'global_program_notice'];
      const results = await Promise.all(
        ids.map((id) => axios.get(`${BACKEND_URL}/api/admin/site-content/${id}`, { withCredentials: true }).then((r) => r.data?.data || {}).catch(() => ({})))
      );
      const [breakdown, tiersD, globalD, heroD, ctaD, aboutD, annD, imgD, progCardsD, foodSecD, hygSecD, noticeD] = results;
      setContent(breakdown);
      setTiers(tiersD);
      setGlobalCountry(globalD);
      setHomeHero(heroD);
      setHomeCta(ctaD);
      setAboutPage(aboutD);
      setAnnouncement(annD);
      setImageConfig(imgD);
      setLocalProgramsCards(progCardsD);
      setFoodBucket(foodSecD);
      setHygieneKit(hygSecD);
      setGlobalNotice(noticeD);
    } catch {
      setContent({}); setTiers({}); setGlobalCountry({}); setHomeHero({}); setHomeCta({}); setAboutPage({}); setAnnouncement({}); setImageConfig({}); setLocalProgramsCards({}); setFoodBucket({}); setHygieneKit({}); setGlobalNotice({});
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${BACKEND_URL}/api/admin/site-content/local_cost_breakdown`, { data: content }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/donation_tiers`, { data: tiers }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/global_country_program_content`, { data: globalCountry }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/home_hero`, { data: homeHero }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/home_cta`, { data: homeCta }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/about_page`, { data: aboutPage }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/site_announcement`, { data: announcement }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/image_display_config`, { data: imageConfig }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/local_programs_cards`, { data: localProgramsCards }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/food_bucket_section`, { data: foodBucket }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/hygiene_kit_section`, { data: hygieneKit }, { withCredentials: true }),
        axios.put(`${BACKEND_URL}/api/admin/site-content/global_program_notice`, { data: globalNotice }, { withCredentials: true }),
      ]);
      alert('Saved successfully!');
      setAuditRefresh((n) => n + 1);
    } catch { alert('Save failed'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>;

  return (
    <div className="space-y-4" data-testid="page-content-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Page Content Editor</h2>
          <p className="text-xs text-[#4B5563]">Edit cost breakdowns, titles, items, and visibility on the Programs page.</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
          data-testid="save-content-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Global Controls */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
        <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">Section Controls</h4>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setContent(prev => ({ ...prev, visible: !prev.visible }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${content.visible !== false ? 'bg-green-500' : 'bg-gray-300'}`}
              data-testid="toggle-breakdown-visible">
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.visible !== false ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
            <span className="text-xs font-medium flex items-center gap-1">
              {content.visible !== false ? <><Eye className="w-3.5 h-3.5 text-green-600" /> Cost Breakdown Visible</> : <><EyeOff className="w-3.5 h-3.5 text-gray-400" /> Cost Breakdown Hidden</>}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setContent(prev => ({ ...prev, show_photos: !prev.show_photos }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${content.show_photos ? 'bg-green-500' : 'bg-gray-300'}`}
              data-testid="toggle-show-photos">
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.show_photos ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
            <span className="text-xs font-medium flex items-center gap-1">
              <Image className="w-3.5 h-3.5" /> {content.show_photos ? 'Photo Placeholders Visible' : 'Photo Placeholders Hidden'}
            </span>
          </div>
        </div>
      </div>

      {/* Section Title / Subtitle */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
        <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">Section Text</h4>
        <div>
          <label className="text-[10px] text-[#4B5563] mb-1 block">Section Title</label>
          <input type="text" value={content.section_title || ''} onChange={(e) => setContent(prev => ({ ...prev, section_title: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] text-[#4B5563] mb-1 block">Section Subtitle</label>
          <textarea value={content.section_subtitle || ''} onChange={(e) => setContent(prev => ({ ...prev, section_subtitle: e.target.value }))}
            rows={2} className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none" />
        </div>
      </div>

      {/* Food Bucket Editor */}
      <BreakdownEditor
        title="Food Bucket Breakdown"
        data={content.food_bucket || {}}
        onChange={(updated) => setContent(prev => ({ ...prev, food_bucket: updated }))}
      />

      {/* Hygiene Kit Editor */}
      <BreakdownEditor
        title="Hygiene Essentials Breakdown"
        data={content.hygiene_kit || {}}
        onChange={(updated) => setContent(prev => ({ ...prev, hygiene_kit: updated }))}
      />

      {/* Local Programs Cards (Programs page) */}
      {localProgramsCards && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="local-programs-cards-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Programs</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Local Programs Cards (Programs Page)</h3>
            </div>
            <p className="text-xs text-[#4B5563]">
              Edit the <strong>red highlighted line</strong> (first line) and the <strong>supporting subtext</strong> on each of the 4 local program cards on /programs.
            </p>
          </div>

          {[
            { key: 'food_assistance', label: 'Food Assistance Program' },
            { key: 'hygiene_essentials', label: 'Hygiene Essentials Distribution' },
            { key: 'emergency_aid', label: 'Emergency Aid Response' },
            { key: 'cpr_training', label: 'Community CPR Training' },
          ].map(({ key, label }) => (
            <div key={key} className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid={`program-card-editor-${key}`}>
              <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">{label}</h4>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Primary line (red highlighted)</label>
                <input
                  type="text"
                  value={localProgramsCards[key]?.impact || ''}
                  onChange={(e) => setLocalProgramsCards((p) => ({ ...p, [key]: { ...(p[key] || {}), impact: e.target.value } }))}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid={`program-${key}-impact-input`}
                />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Supporting subtext (italic)</label>
                <textarea
                  value={localProgramsCards[key]?.supporting || ''}
                  onChange={(e) => setLocalProgramsCards((p) => ({ ...p, [key]: { ...(p[key] || {}), supporting: e.target.value } }))}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none"
                  data-testid={`program-${key}-supporting-input`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Food Bucket Section Editor */}
      {foodBucket && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="food-bucket-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Programs</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Food Bucket Flyer Section</h3>
            </div>
            <p className="text-xs text-[#4B5563]">Edit the flyer-style Food Bucket section shown on /programs.</p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Title</label>
              <input type="text" value={foodBucket.title || ''}
                onChange={(e) => setFoodBucket((p) => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="food-bucket-title" />
            </div>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Description</label>
              <textarea value={foodBucket.description || ''}
                onChange={(e) => setFoodBucket((p) => ({ ...p, description: e.target.value }))}
                rows={2} className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none"
                data-testid="food-bucket-description" />
            </div>

            {/* Value points */}
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Value Points (4 max)</label>
              <div className="space-y-2">
                {(foodBucket.value_points || []).map((vp, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr] gap-2" data-testid={`food-vp-${i}`}>
                    <input type="text" value={vp.heading || ''} placeholder="Heading"
                      onChange={(e) => {
                        const arr = [...(foodBucket.value_points || [])];
                        arr[i] = { ...arr[i], heading: e.target.value };
                        setFoodBucket((p) => ({ ...p, value_points: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                    <input type="text" value={vp.text || ''} placeholder="Text"
                      onChange={(e) => {
                        const arr = [...(foodBucket.value_points || [])];
                        arr[i] = { ...arr[i], text: e.target.value };
                        setFoodBucket((p) => ({ ...p, value_points: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>

            {/* Items */}
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">What's Inside — Item Names (comma-separated)</label>
              <input type="text" value={(foodBucket.items || []).map(it => it.name).join(', ')}
                onChange={(e) => {
                  const arr = e.target.value.split(',').map(s => ({ name: s.trim() })).filter(it => it.name);
                  setFoodBucket((p) => ({ ...p, items: arr }));
                }}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="food-bucket-items" />
            </div>

            {/* Impact tiers */}
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Impact Tiers (families → $)</label>
              <div className="grid grid-cols-4 gap-2">
                {(foodBucket.impact_tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-2 gap-1" data-testid={`food-tier-${i}`}>
                    <input type="number" value={t.families || 0} placeholder="Fam"
                      onChange={(e) => {
                        const arr = [...(foodBucket.impact_tiers || [])];
                        arr[i] = { ...arr[i], families: parseInt(e.target.value) || 0 };
                        setFoodBucket((p) => ({ ...p, impact_tiers: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                    <input type="number" step="0.01" value={t.amount || 0} placeholder="$"
                      onChange={(e) => {
                        const arr = [...(foodBucket.impact_tiers || [])];
                        arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 };
                        setFoodBucket((p) => ({ ...p, impact_tiers: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">CTA Label</label>
                <input type="text" value={foodBucket.cta_amount_label || ''}
                  onChange={(e) => setFoodBucket((p) => ({ ...p, cta_amount_label: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="food-bucket-cta-label" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">CTA Subtext</label>
                <input type="text" value={foodBucket.cta_sub_text || ''}
                  onChange={(e) => setFoodBucket((p) => ({ ...p, cta_sub_text: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="food-bucket-cta-sub" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hygiene Kit Section Editor */}
      {hygieneKit && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="hygiene-kit-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Programs</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Hygiene Kit Flyer Section</h3>
            </div>
            <p className="text-xs text-[#4B5563]">Edit the flyer-style Hygiene Essentials Kit section on /programs, including the 3 kit cards (Men / Women / Children).</p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Title</label>
              <input type="text" value={hygieneKit.title || ''}
                onChange={(e) => setHygieneKit((p) => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="hygiene-kit-title" />
            </div>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Description</label>
              <textarea value={hygieneKit.description || ''}
                onChange={(e) => setHygieneKit((p) => ({ ...p, description: e.target.value }))}
                rows={2} className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none"
                data-testid="hygiene-kit-description" />
            </div>

            {/* Value points */}
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Value Points (4 max)</label>
              <div className="space-y-2">
                {(hygieneKit.value_points || []).map((vp, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr] gap-2" data-testid={`hyg-vp-${i}`}>
                    <input type="text" value={vp.heading || ''} placeholder="Heading"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.value_points || [])];
                        arr[i] = { ...arr[i], heading: e.target.value };
                        setHygieneKit((p) => ({ ...p, value_points: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                    <input type="text" value={vp.text || ''} placeholder="Text"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.value_points || [])];
                        arr[i] = { ...arr[i], text: e.target.value };
                        setHygieneKit((p) => ({ ...p, value_points: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>

            {/* Items (top icon row) */}
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Top "Essentials" Icons — Names (comma-separated)</label>
              <input type="text" value={(hygieneKit.items || []).map(it => it.name).join(', ')}
                onChange={(e) => {
                  const arr = e.target.value.split(',').map(s => ({ name: s.trim() })).filter(it => it.name);
                  setHygieneKit((p) => ({ ...p, items: arr }));
                }}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="hygiene-kit-items" />
            </div>

            {/* 3 Kits */}
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Kits (Men / Women / Children)</label>
              <div className="grid md:grid-cols-3 gap-2">
                {(hygieneKit.kits || []).map((kit, i) => (
                  <div key={i} className="border border-[#E5E7EB] rounded p-2 space-y-1.5" data-testid={`hyg-kit-editor-${i}`}>
                    <input type="text" value={kit.name || ''} placeholder="Kit name"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.kits || [])];
                        arr[i] = { ...arr[i], name: e.target.value };
                        setHygieneKit((p) => ({ ...p, kits: arr }));
                      }}
                      className="w-full px-2 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                    <input type="number" step="0.01" value={kit.price || 0} placeholder="Price"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.kits || [])];
                        arr[i] = { ...arr[i], price: parseFloat(e.target.value) || 0 };
                        setHygieneKit((p) => ({ ...p, kits: arr }));
                      }}
                      className="w-full px-2 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                    <select value={kit.color || 'blue'}
                      onChange={(e) => {
                        const arr = [...(hygieneKit.kits || [])];
                        arr[i] = { ...arr[i], color: e.target.value };
                        setHygieneKit((p) => ({ ...p, kits: arr }));
                      }}
                      className="w-full px-2 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none">
                      <option value="blue">Blue</option>
                      <option value="pink">Pink</option>
                      <option value="green">Green</option>
                    </select>
                    <textarea value={(kit.items || []).join('\n')} placeholder="One item per line"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.kits || [])];
                        arr[i] = { ...arr[i], items: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) };
                        setHygieneKit((p) => ({ ...p, kits: arr }));
                      }}
                      rows={5}
                      className="w-full px-2 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none font-mono" />
                    <input type="text" value={kit.tagline || ''} placeholder="Tagline"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.kits || [])];
                        arr[i] = { ...arr[i], tagline: e.target.value };
                        setHygieneKit((p) => ({ ...p, kits: arr }));
                      }}
                      className="w-full px-2 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                    <div className="pt-1.5 mt-1 border-t border-[#E5E7EB] space-y-1.5">
                      <p className="text-[9px] font-bold uppercase tracking-wider text-[#BA1826]">Donate Button</p>
                      <input type="number" step="1" min="1" value={kit.donate_amount ?? ''} placeholder="Donate amount ($)"
                        onChange={(e) => {
                          const arr = [...(hygieneKit.kits || [])];
                          arr[i] = { ...arr[i], donate_amount: parseInt(e.target.value) || 0 };
                          setHygieneKit((p) => ({ ...p, kits: arr }));
                        }}
                        className="w-full px-2 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                        data-testid={`hyg-kit-donate-amount-${i}`} />
                      <input type="text" value={kit.donate_label || ''} placeholder="Button label (e.g. Fund a Kit — $25)"
                        onChange={(e) => {
                          const arr = [...(hygieneKit.kits || [])];
                          arr[i] = { ...arr[i], donate_label: e.target.value };
                          setHygieneKit((p) => ({ ...p, kits: arr }));
                        }}
                        className="w-full px-2 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                        data-testid={`hyg-kit-donate-label-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Impact tiers */}
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Impact Tiers (# kits → sub label)</label>
              <div className="grid md:grid-cols-2 gap-2">
                {(hygieneKit.impact_tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-[80px_1fr] gap-1" data-testid={`hyg-tier-${i}`}>
                    <input type="number" value={t.kits || 0} placeholder="Kits"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.impact_tiers || [])];
                        arr[i] = { ...arr[i], kits: parseInt(e.target.value) || 0 };
                        setHygieneKit((p) => ({ ...p, impact_tiers: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                    <input type="text" value={t.sub_label || ''} placeholder="Label"
                      onChange={(e) => {
                        const arr = [...(hygieneKit.impact_tiers || [])];
                        arr[i] = { ...arr[i], sub_label: e.target.value };
                        setHygieneKit((p) => ({ ...p, impact_tiers: arr }));
                      }}
                      className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">CTA Label</label>
                <input type="text" value={hygieneKit.cta_amount_label || ''}
                  onChange={(e) => setHygieneKit((p) => ({ ...p, cta_amount_label: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hygiene-kit-cta-label" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">CTA Subtext</label>
                <input type="text" value={hygieneKit.cta_sub_text || ''}
                  onChange={(e) => setHygieneKit((p) => ({ ...p, cta_sub_text: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hygiene-kit-cta-sub" />
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Site-Wide Announcement Banner (Phase C) */}
      {/* Image Placeholder Controls (Phase 3.5) */}
      {imageConfig && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="image-display-config-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#0A0A0A] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Display</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Image Placeholder Controls</h3>
            </div>
            <p className="text-xs text-[#4B5563]">
              Choose whether empty image slots show a gray placeholder or are hidden completely on the public site. Each toggle below controls a different area. Defaults to <strong>hidden</strong> for the cleanest look.
            </p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-2">
            {[
              { key: 'hide_empty_event_images', label: 'Event images (event page flyers + event item photos)' },
              { key: 'hide_empty_product_images', label: 'Shop & merch product images' },
              { key: 'hide_empty_galleries', label: 'Photo gallery empty states' },
              { key: 'hide_empty_country_images', label: 'Country program images' },
              { key: 'hide_empty_update_images', label: 'Community update images' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between text-xs py-1.5 border-b border-[#F1F5F9] last:border-0">
                <span className="text-[#0A0A0A]">{label}</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className={`text-[10px] font-semibold ${imageConfig[key] ? 'text-[#BA1826]' : 'text-[#94A3B8]'}`}>
                    {imageConfig[key] ? 'Hidden when empty' : 'Show placeholder'}
                  </span>
                  <button type="button"
                    onClick={() => setImageConfig((p) => ({ ...p, [key]: !p[key] }))}
                    className={`relative w-10 h-5 rounded-full transition-colors ${imageConfig[key] ? 'bg-[#BA1826]' : 'bg-[#D1D5DB]'}`}
                    data-testid={`toggle-${key}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${imageConfig[key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}


      {announcement && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="announcement-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#0A0A0A] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Banner</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Site-Wide Announcement</h3>
            </div>
            <p className="text-xs text-[#4B5563]">Appears at the very top of every public page. Visitors can dismiss it.</p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setAnnouncement((p) => ({ ...p, enabled: !p.enabled }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${announcement.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                data-testid="toggle-announcement-enabled">
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${announcement.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
              <span className="text-xs font-medium">{announcement.enabled ? 'Banner enabled (live)' : 'Banner disabled (hidden)'}</span>
            </div>

            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Message</label>
              <textarea value={announcement.message || ''} onChange={(e) => setAnnouncement((p) => ({ ...p, message: e.target.value }))}
                rows={2} maxLength={250}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none"
                data-testid="announcement-message-input"
                placeholder="e.g., Year-end giving now open — Donate before Dec 31 →" />
              <p className="text-[9px] text-[#94A3B8] mt-1">{(announcement.message || '').length}/250 — keep it short. A new message re-appears even for users who dismissed the previous one.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Link Label (optional)</label>
                <input type="text" value={announcement.link_label || ''} onChange={(e) => setAnnouncement((p) => ({ ...p, link_label: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="announcement-link-label-input" placeholder='e.g., "Donate now"' />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Link URL (optional)</label>
                <input type="text" value={announcement.link_url || ''} onChange={(e) => setAnnouncement((p) => ({ ...p, link_url: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="announcement-link-url-input" placeholder="/donate" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Style</label>
                <select value={announcement.style || 'info'} onChange={(e) => setAnnouncement((p) => ({ ...p, style: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="announcement-style-select">
                  <option value="info">Info (black)</option>
                  <option value="success">Success (green)</option>
                  <option value="warning">Warning (amber)</option>
                  <option value="urgent">Urgent (red)</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <input type="checkbox" id="announcement-dismissible" checked={announcement.dismissible !== false}
                  onChange={(e) => setAnnouncement((p) => ({ ...p, dismissible: e.target.checked }))}
                  data-testid="announcement-dismissible-checkbox" />
                <label htmlFor="announcement-dismissible" className="text-xs text-[#0A0A0A]">Allow visitors to dismiss</label>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block flex items-center gap-2">
                <span>Show After (optional)</span>
                {announcement.show_after && (() => {
                  const todayStr = new Date().toISOString().slice(0, 10);
                  if (todayStr < announcement.show_after) {
                    return <span className="text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded" data-testid="announcement-scheduled-label">Scheduled</span>;
                  }
                  return null;
                })()}
              </label>
              <div className="flex items-center gap-2">
                <input type="date" value={announcement.show_after || ''}
                  onChange={(e) => setAnnouncement((p) => ({ ...p, show_after: e.target.value || null }))}
                  className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="announcement-show-after-input" />
                {announcement.show_after && (
                  <button type="button" onClick={() => setAnnouncement((p) => ({ ...p, show_after: null }))}
                    className="text-[10px] text-[#9CA3AF] hover:text-[#0A0A0A] underline"
                    data-testid="announcement-clear-show-after">Clear</button>
                )}
              </div>
              <p className="text-[9px] text-[#94A3B8] mt-1">Optionally schedule when this appears. Leave blank to show immediately.</p>
            </div>

            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block flex items-center gap-2">
                <span>Hide After (optional)</span>
                {announcement.hide_after && (() => {
                  const todayStr = new Date().toISOString().slice(0, 10);
                  if (todayStr >= announcement.hide_after) {
                    return <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded" data-testid="announcement-expired-label">Expired (auto-hidden)</span>;
                  }
                  return null;
                })()}
              </label>
              <div className="flex items-center gap-2">
                <input type="date" value={announcement.hide_after || ''}
                  onChange={(e) => setAnnouncement((p) => ({ ...p, hide_after: e.target.value || null }))}
                  className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="announcement-hide-after-input" />
                {announcement.hide_after && (
                  <button type="button" onClick={() => setAnnouncement((p) => ({ ...p, hide_after: null }))}
                    className="text-[10px] text-[#9CA3AF] hover:text-[#0A0A0A] underline"
                    data-testid="announcement-clear-hide-after">Clear</button>
                )}
              </div>
              <p className="text-[9px] text-[#94A3B8] mt-1">Optional: automatically hide this banner after a specific date. Manual toggle still overrides everything.</p>
            </div>
          </div>
        </div>
      )}

      {/* Global Programs Notice — temporary expansion message, on /international page */}
      {globalNotice && (
        <div className="space-y-3 bg-white border-2 border-[#BA1826] rounded-lg p-5" data-testid="global-notice-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Notice</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Global Programs Page — Expansion Notice</h3>
            </div>
            <p className="text-xs text-[#4B5563]">Soft informational note shown beneath "Our work extends beyond New York City..." on the Global Programs page. Easy to remove when no longer needed — just toggle off.</p>
          </div>

          <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setGlobalNotice((p) => ({ ...p, enabled: !p.enabled }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${globalNotice.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                data-testid="toggle-global-notice-enabled">
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${globalNotice.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
              <span className="text-xs font-medium">{globalNotice.enabled ? 'Notice visible' : 'Notice hidden'}</span>
            </div>

            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Label (small caps)</label>
              <input type="text" value={globalNotice.label || ''} maxLength={40}
                onChange={(e) => setGlobalNotice((p) => ({ ...p, label: e.target.value }))}
                className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="global-notice-label-input" placeholder="e.g., Upcoming Expansion" />
            </div>

            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Message</label>
              <textarea value={globalNotice.message || ''} rows={3} maxLength={400}
                onChange={(e) => setGlobalNotice((p) => ({ ...p, message: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none"
                data-testid="global-notice-message-input"
                placeholder="We are currently expanding our international programs..." />
              <p className="text-[9px] text-[#94A3B8] mt-1">{(globalNotice.message || '').length}/400</p>
            </div>

            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block flex items-center gap-2">
                <span>Show After (optional)</span>
                {globalNotice.show_after && (() => {
                  const todayStr = new Date().toISOString().slice(0, 10);
                  if (todayStr < globalNotice.show_after) {
                    return <span className="text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded" data-testid="global-notice-scheduled-label">Scheduled</span>;
                  }
                  return null;
                })()}
              </label>
              <div className="flex items-center gap-2">
                <input type="date" value={globalNotice.show_after || ''}
                  onChange={(e) => setGlobalNotice((p) => ({ ...p, show_after: e.target.value || null }))}
                  className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="global-notice-show-after-input" />
                {globalNotice.show_after && (
                  <button type="button" onClick={() => setGlobalNotice((p) => ({ ...p, show_after: null }))}
                    className="text-[10px] text-[#9CA3AF] hover:text-[#0A0A0A] underline"
                    data-testid="global-notice-clear-show-after">Clear</button>
                )}
              </div>
              <p className="text-[9px] text-[#94A3B8] mt-1">Optionally schedule when this appears. Leave blank to show immediately.</p>
            </div>

            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block flex items-center gap-2">
                <span>Hide After (optional)</span>
                {globalNotice.hide_after && (() => {
                  const todayStr = new Date().toISOString().slice(0, 10);
                  if (todayStr >= globalNotice.hide_after) {
                    return <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded" data-testid="global-notice-expired-label">Expired (auto-hidden)</span>;
                  }
                  return null;
                })()}
              </label>
              <div className="flex items-center gap-2">
                <input type="date" value={globalNotice.hide_after || ''}
                  onChange={(e) => setGlobalNotice((p) => ({ ...p, hide_after: e.target.value || null }))}
                  className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="global-notice-hide-after-input" />
                {globalNotice.hide_after && (
                  <button type="button" onClick={() => setGlobalNotice((p) => ({ ...p, hide_after: null }))}
                    className="text-[10px] text-[#9CA3AF] hover:text-[#0A0A0A] underline"
                    data-testid="global-notice-clear-hide-after">Clear</button>
                )}
              </div>
              <p className="text-[9px] text-[#94A3B8] mt-1">Optional: automatically hide this notice after a specific date. Manual toggle still overrides everything.</p>
            </div>
          </div>
        </div>
      )}

      {/* Home Hero Content (Phase C) */}
      {homeHero && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="home-hero-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Home</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Homepage Hero Section</h3>
            </div>
            <p className="text-xs text-[#4B5563]">The first thing visitors see. Edit the headline, subtitle, and both CTA buttons.</p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Eyebrow (small red text above headline)</label>
              <input type="text" value={homeHero.eyebrow || ''} onChange={(e) => setHomeHero((p) => ({ ...p, eyebrow: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="hero-eyebrow-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Headline</label>
                <input type="text" value={homeHero.title || ''} onChange={(e) => setHomeHero((p) => ({ ...p, title: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hero-title-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Headline Highlight (red)</label>
                <input type="text" value={homeHero.title_highlight || ''} onChange={(e) => setHomeHero((p) => ({ ...p, title_highlight: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hero-title-highlight-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Subtitle (primary, bold)</label>
              <input type="text" value={homeHero.subtitle_primary || ''} onChange={(e) => setHomeHero((p) => ({ ...p, subtitle_primary: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="hero-subtitle-primary-input" />
            </div>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Subtitle (secondary, full paragraph)</label>
              <textarea value={homeHero.subtitle_secondary || ''} onChange={(e) => setHomeHero((p) => ({ ...p, subtitle_secondary: e.target.value }))}
                rows={2} className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none"
                data-testid="hero-subtitle-secondary-input" />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#F1F5F9]">
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Primary CTA Label</label>
                <input type="text" value={homeHero.cta_primary_label || ''} onChange={(e) => setHomeHero((p) => ({ ...p, cta_primary_label: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hero-cta-primary-label-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Primary CTA URL</label>
                <input type="text" value={homeHero.cta_primary_url || ''} onChange={(e) => setHomeHero((p) => ({ ...p, cta_primary_url: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hero-cta-primary-url-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Secondary CTA Label (optional)</label>
                <input type="text" value={homeHero.cta_secondary_label || ''} onChange={(e) => setHomeHero((p) => ({ ...p, cta_secondary_label: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hero-cta-secondary-label-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Secondary CTA URL</label>
                <input type="text" value={homeHero.cta_secondary_url || ''} onChange={(e) => setHomeHero((p) => ({ ...p, cta_secondary_url: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="hero-cta-secondary-url-input" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Home CTA Section (Phase C) */}
      {homeCta && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="home-cta-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Home</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Homepage CTA Section (Red Band)</h3>
            </div>
            <p className="text-xs text-[#4B5563]">The red "Ready to Make a Difference?" banner near the bottom of the homepage.</p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Headline</label>
              <input type="text" value={homeCta.headline || ''} onChange={(e) => setHomeCta((p) => ({ ...p, headline: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="home-cta-headline-input" />
            </div>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Subtitle</label>
              <textarea value={homeCta.subtitle || ''} onChange={(e) => setHomeCta((p) => ({ ...p, subtitle: e.target.value }))}
                rows={2} className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none"
                data-testid="home-cta-subtitle-input" />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#F1F5F9]">
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Primary CTA Label</label>
                <input type="text" value={homeCta.cta_primary_label || ''} onChange={(e) => setHomeCta((p) => ({ ...p, cta_primary_label: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="home-cta-primary-label-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Primary CTA URL</label>
                <input type="text" value={homeCta.cta_primary_url || ''} onChange={(e) => setHomeCta((p) => ({ ...p, cta_primary_url: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="home-cta-primary-url-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Secondary CTA Label (optional)</label>
                <input type="text" value={homeCta.cta_secondary_label || ''} onChange={(e) => setHomeCta((p) => ({ ...p, cta_secondary_label: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="home-cta-secondary-label-input" />
              </div>
              <div>
                <label className="text-[10px] text-[#4B5563] mb-1 block">Secondary CTA URL</label>
                <input type="text" value={homeCta.cta_secondary_url || ''} onChange={(e) => setHomeCta((p) => ({ ...p, cta_secondary_url: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid="home-cta-secondary-url-input" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Page Content (Phase C) */}
      {aboutPage && (
        <div className="space-y-3 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="about-page-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">About</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">About Page Content</h3>
            </div>
            <p className="text-xs text-[#4B5563]">Edit the About page title, "Who We Are", Mission, and Vision copy.</p>
          </div>

          {/* Page Title */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">Page Header</h4>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Page Title (red header bar)</label>
              <input type="text" value={aboutPage.page_title || ''} onChange={(e) => setAboutPage((p) => ({ ...p, page_title: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                data-testid="about-page-title-input" />
            </div>
          </div>

          {/* Who We Are */}
          <ParagraphsEditor
            title="Who We Are Section"
            testIdPrefix="who-we-are"
            eyebrow={aboutPage.who_we_are_eyebrow || ''}
            heading={aboutPage.who_we_are_heading || ''}
            paragraphs={aboutPage.who_we_are_paragraphs || []}
            onEyebrowChange={(v) => setAboutPage((p) => ({ ...p, who_we_are_eyebrow: v }))}
            onHeadingChange={(v) => setAboutPage((p) => ({ ...p, who_we_are_heading: v }))}
            onParagraphsChange={(v) => setAboutPage((p) => ({ ...p, who_we_are_paragraphs: v }))}
          />

          {/* Mission */}
          <ParagraphsEditor
            title="Our Mission"
            testIdPrefix="about-mission"
            heading={aboutPage.mission_heading || ''}
            paragraphs={aboutPage.mission_paragraphs || []}
            onHeadingChange={(v) => setAboutPage((p) => ({ ...p, mission_heading: v }))}
            onParagraphsChange={(v) => setAboutPage((p) => ({ ...p, mission_paragraphs: v }))}
          />

          {/* Vision */}
          <ParagraphsEditor
            title="Our Vision"
            testIdPrefix="about-vision"
            heading={aboutPage.vision_heading || ''}
            paragraphs={aboutPage.vision_paragraphs || []}
            onHeadingChange={(v) => setAboutPage((p) => ({ ...p, vision_heading: v }))}
            onParagraphsChange={(v) => setAboutPage((p) => ({ ...p, vision_paragraphs: v }))}
          />
        </div>
      )}


      {/* Global Country Program Content */}
      {globalCountry && (
        <div className="space-y-4 bg-[#FEF9E7] border-2 border-[#F4D03F] rounded-lg p-5" data-testid="global-country-program-editor">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-[#BA1826] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">Global</span>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Country Program Content</h3>
            </div>
            <p className="text-xs text-[#4B5563]">
              Manage Food Bucket & Hygiene Essentials in one place. These values cascade to every country program where "Use Global Program Content" is enabled.
            </p>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">Section Text</h4>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Section Title</label>
              <input type="text" value={globalCountry.section_title || ''}
                onChange={(e) => setGlobalCountry(prev => ({ ...prev, section_title: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Section Subtitle</label>
              <textarea value={globalCountry.section_subtitle || ''}
                onChange={(e) => setGlobalCountry(prev => ({ ...prev, section_subtitle: e.target.value }))}
                rows={2} className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none resize-none" />
            </div>
          </div>

          <BreakdownEditor
            title="Global Food Bucket"
            showSize
            data={globalCountry.food_bucket || {}}
            onChange={(updated) => setGlobalCountry(prev => ({ ...prev, food_bucket: updated }))}
          />

          <BreakdownEditor
            title="Global Hygiene Essentials Kit"
            showSize
            data={globalCountry.hygiene_kit || {}}
            onChange={(updated) => setGlobalCountry(prev => ({ ...prev, hygiene_kit: updated }))}
          />

          <div className="bg-[#BA1826] text-white rounded-lg p-4 flex items-center justify-between" data-testid="global-cost-per-family">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-white/80 font-semibold">Cost per Family (Auto)</p>
              <p className="text-xs text-white/70 mt-0.5">Food Bucket + Hygiene Essentials — cascades to every country program using Global Content.</p>
            </div>
            <div className="flex items-center gap-1">
              <DollarSign className="w-5 h-5 text-white/80" />
              <span className="text-3xl font-bold">
                {(
                  (globalCountry.food_bucket?.total || 0) +
                  (globalCountry.hygiene_kit?.total || 0)
                ).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Audit Log — lives inside the Global card so it's contextual */}
          <GlobalContentAuditLog refreshSignal={auditRefresh} />
        </div>
      )}

      {/* Donation Tiers Editor */}
      {tiers && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
          <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">Donation Tiers (Donate Page)</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Section Title</label>
              <input type="text" value={tiers.section_title || ''} onChange={(e) => setTiers(prev => ({ ...prev, section_title: e.target.value }))}
                className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[#4B5563] mb-1 block">Anchor Text (e.g. "$100 = 1 Family")</label>
              <input type="text" value={tiers.anchor_text || ''} onChange={(e) => setTiers(prev => ({ ...prev, anchor_text: e.target.value }))}
                className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#4B5563] mb-1 block">Subtitle</label>
            <input type="text" value={tiers.section_subtitle || ''} onChange={(e) => setTiers(prev => ({ ...prev, section_subtitle: e.target.value }))}
              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[#4B5563] mb-1 block">Microcopy (below cards)</label>
            <input type="text" value={tiers.microcopy || ''} onChange={(e) => setTiers(prev => ({ ...prev, microcopy: e.target.value }))}
              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[80px_1fr_80px_30px] gap-1 text-[9px] font-semibold text-[#4B5563] uppercase px-1">
              <span>Amount</span><span>Label</span><span>Popular</span><span></span>
            </div>
            {(tiers.tiers || []).map((tier, idx) => (
              <div key={idx} className="grid grid-cols-[80px_1fr_80px_30px] gap-1 items-center">
                <input type="number" value={tier.amount} onChange={(e) => {
                  const updated = [...(tiers.tiers || [])];
                  updated[idx] = { ...updated[idx], amount: parseFloat(e.target.value) || 0 };
                  setTiers(prev => ({ ...prev, tiers: updated }));
                }} className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none text-center" />
                <input type="text" value={tier.label} onChange={(e) => {
                  const updated = [...(tiers.tiers || [])];
                  updated[idx] = { ...updated[idx], label: e.target.value };
                  setTiers(prev => ({ ...prev, tiers: updated }));
                }} className="px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none" />
                <button type="button" onClick={() => {
                  const updated = [...(tiers.tiers || [])];
                  updated[idx] = { ...updated[idx], popular: !updated[idx].popular };
                  setTiers(prev => ({ ...prev, tiers: updated }));
                }} className={`px-2 py-1.5 text-[9px] font-semibold rounded ${tier.popular ? 'bg-[#BA1826] text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {tier.popular ? 'Popular' : 'Normal'}
                </button>
                <button type="button" onClick={() => {
                  const updated = (tiers.tiers || []).filter((_, i) => i !== idx);
                  setTiers(prev => ({ ...prev, tiers: updated }));
                }} className="text-red-400 hover:text-red-600 p-1">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => {
              const updated = [...(tiers.tiers || []), { id: `tier_${Date.now()}`, amount: 0, label: '', popular: false, enabled: true }];
              setTiers(prev => ({ ...prev, tiers: updated }));
            }} className="text-[10px] text-[#BA1826] font-semibold hover:underline flex items-center gap-1 mt-1">
              <Plus className="w-3 h-3" /> Add Tier
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPageContentTab;
